import React, { useState } from "react";
import Table from "react-bootstrap/Table";
import EditIcon from "../../../assets/images/edit.png";
import DeleteIcon from "../../../assets/images/delete.png";
import SearchBar from "../../common/components/SearchBar";
import { Button } from "../../common/Button";
import { Link, useNavigate } from "react-router-dom";
import { Skeleton, TableCell, Tooltip } from "@mui/material";
import Select from "react-select";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { failed, success } from "../../common/Toastify";
import { Spinner } from "react-bootstrap";
import EditCategory from "./EditCategory";
import { deleteQuestionCategory } from "../../api/Questionnaire";
import { useQuestionCategoryQuery } from "../../../hooks/ReactQueryHooks/useQuestionCategoryQuery";
import moment from "moment";
import { useSelector } from "react-redux";
import ViewIcon from "../../../assets/images/view.png";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { questionnaireForOptions } from "./Constant";
import { useOrgQuery } from "../../../hooks/ReactQueryHooks/useOrgQuery";

function CategoryList() {
  const { getConfirmation } = useConfirmDialog();
  const userType = useSelector((state) => state?.auth?.user?.["cognito:groups"]);
  const userOrg = useSelector((state) => state?.auth?.user?.["custom:orgId"]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(userOrg);

  const [modalShow, setModalShow] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [filterData, setFilterData] = useState([]);
  const [programList, setProgramList] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [sortBy, setSortBy] = useState("AscName");
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const totalRows = filterData.length;

  const handleFilterData = (data) => {
    setFilterData(data);
    setPage(0);
  };

  useOrgQuery({
    onSuccess: (data) => {
      setOrganizations(data?.map((org) => ({ label: org?.name, value: org?.id })) || []);
    },
    refetchOnWindowFocus: false,
  });

  // React Query – refetches automatically when selectedOrgId changes
  const { isLoading, isFetching } = useQuestionCategoryQuery({
    orgId: selectedOrgId,
    onSuccess: (data) => {
      setProgramList(data || []);
      setFilterData(data || []);
      setPage(0);
    },
  });

  const handleOrgChange = (opt) => {
    setSelectedOrgId(opt?.value || null); // no manual refetch needed
  };

  const handleShow = (data, isReadOnly) => {
    setModalShow(modalShow ? false : data);
    setReadOnly(isReadOnly);
  };

  const handleDelete = async (id, entryName) => {
    const confirmed = await getConfirmation({
      title: "Attention!",
      entryName,
      actionBtnTitle: "Delete",
    });
    if (confirmed) {
      setDeleting(id);
      deleteQuestionCategory(id)
        .then(() => {
          setFilterData((prev) => prev.filter((d) => d.id !== id));
          navigate("/app/manage-category");
          success("Question Category Deleted!");
        })
        .catch((res) => failed(res?.response?.data?.message || res?.response?.data?.error || res.message))
        .finally(() => setDeleting(false));
    }
  };

  const sortFilterData = (sort_by) => {
    setSortBy(sort_by);
    switch (sort_by) {
      case "AscName":
        setFilterData((prev) => [...prev].sort((a, b) => (a.categoryName > b.categoryName ? 1 : a.categoryName < b.categoryName ? -1 : 0)));
        break;
      case "DscName":
        setFilterData((prev) => [...prev].sort((a, b) => (a.categoryName < b.categoryName ? 1 : a.categoryName > b.categoryName ? -1 : 0)));
        break;
      default:
        break;
    }
  };

  const getCategoryTypeName = (categoryType) => {
    const category = questionnaireForOptions.find((option) => option.value === categoryType);
    return category ? category.name : categoryType;
  };

  return (
    <React.Fragment>
      <section className="common-listing">
        {modalShow ? <EditCategory modalShow={modalShow} handleShow={handleShow} refreshTable={() => {}} isReadOnly={readOnly} /> : null}

        <div className="heading-wrap h-change">
          <SearchBar page={page} setPage={setPage} list={programList} filterData={handleFilterData} />
          <div className="right-wrap ml-auto" style={{ margin: "left" }}>
            <Select
              options={organizations}
              isLoading={organizations.length <= 1}
              isDisabled={organizations.length <= 1}
              onChange={handleOrgChange}
              className="search-select"
              classNamePrefix="select"
              placeholder="Select Organization..."
              value={organizations.find((o) => o.value === selectedOrgId) || null}
            />
            <Button onClick={() => navigate("/app/create-category")} className="custom-btn" style={{ width: "auto" }}>
              Create
            </Button>
          </div>
        </div>

        <div className="table-wrap">
          <Table responsive>
            <thead>
              <tr>
                <th style={{ cursor: "pointer" }} onClick={() => sortFilterData(sortBy === "AscName" ? "DscName" : "AscName")}>
                  Category {sortBy === "AscName" ? <ArrowDropUp /> : <ArrowDropDown />}
                </th>
                <th>Workflow</th>
                <th style={{ textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading || isFetching ? (
                [1, 2, 3, 4, 5, 6, 7].map((val) => (
                  <tr key={val}>
                    <td>
                      <Skeleton animation="wave" />
                    </td>
                    <td>
                      <Skeleton animation="wave" />
                    </td>
                    <td>
                      <Skeleton animation="wave" />
                    </td>
                  </tr>
                ))
              ) : filterData?.length > 0 ? (
                filterData.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((data) => {
                  const hasAccess = userType?.includes("Super-Admin") ? true : !!!data?.isDefault;
                  return (
                    <tr key={data?.id}>
                      <td>{data?.categoryName}</td>
                      <td>{getCategoryTypeName(data?.categoryType)}</td>
                      <td className="action-wrap-container">
                        <div className="actions-styles action-wrap">
                          {hasAccess ? (
                            <Tooltip title={"Edit"}>
                              <Link to="" variant="primary" onClick={() => handleShow(data, false)} className="success-btn">
                                <img src={EditIcon} alt="Edit" />
                              </Link>
                            </Tooltip>
                          ) : (
                            <Tooltip title={"View"}>
                              <Link to="" variant="primary" onClick={() => handleShow(data, true)} className="success-btn">
                                <img src={ViewIcon} alt="View" />
                              </Link>
                            </Tooltip>
                          )}
                          <Tooltip title={hasAccess ? "Delete" : "You dont have permission"}>
                            <Link
                              to=""
                              variant="primary"
                              onClick={() => hasAccess && (deleting ? null : handleDelete(data?.id, data?.title))}
                              className="delete-btn"
                              style={{ opacity: hasAccess ? "1" : "0.2" }}
                            >
                              {deleting === data?.id ? (
                                <Spinner size="lg" className="text-current" style={{ color: "white", background: "transparent", margin: "0" }} />
                              ) : (
                                <img src={DeleteIcon} alt="Delete" />
                              )}
                            </Link>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <TableCell colSpan="5" style={{ textAlign: "center" }}>
                    No Data Available
                  </TableCell>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {filterData?.length > 0 ? (
          <CustomPagination tableData={filterData} totalRows={totalRows} rowsPerPage={rowsPerPage} page={page} handlePage={(pageNo) => setPage(pageNo)} />
        ) : null}
      </section>
    </React.Fragment>
  );
}

export default CategoryList;